import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCurrentUser } from '@/hooks/use-current-user';
import { getConsultantProfile } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Eye, FileSignature, FileText, LayoutGrid, Monitor, Settings, Users, } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSettingsCategoryVisible } from './settingsAccess';
const categoryIcons = {
    general: Settings,
    display: Monitor,
    viewer: Eye,
    hanging_protocols: LayoutGrid,
    reporting: FileText,
    tc_reporting: FileSignature,
    schedule: CalendarDays,
    users_permissions: Users,
    tc_groups: Users,
};
const categories = [
    { key: 'general', labelKey: 'category.general' },
    { key: 'display', labelKey: 'category.display' },
    { key: 'viewer', labelKey: 'category.viewer' },
    { key: 'hanging_protocols', labelKey: 'category.hangingProtocols' },
    { key: 'reporting', labelKey: 'category.reporting' },
    { key: 'tc_reporting', labelKey: 'category.tcReporting' },
    { key: 'schedule', labelKey: 'category.schedule' },
    { key: 'users_permissions', labelKey: 'category.usersPermissions' },
    { key: 'tc_groups', labelKey: 'category.tcGroups' },
];
export function SettingsSidebar({ activeCategory, onCategoryChange, variant = 'vertical', className, }) {
    const { t } = useTranslation('settings');
    const { canManageUsers, currentUser, isSuperAdmin } = useCurrentUser();
    const isClinicAdmin = currentUser?.clinicRoles.some((cr) => cr.roleName === 'Clinic Admin') ?? false;
    const { data: tcProfile } = useQuery({
        queryKey: ['consultant-profile'],
        queryFn: getConsultantProfile,
        staleTime: 5 * 60_000,
    });
    const isTcGroupAdmin = tcProfile?.groups?.some((g) => g.role === 'admin') ?? false;
    const isTcGroupMember = (tcProfile?.groups?.length ?? 0) > 0;
    const visibleCategories = categories.filter(({ key }) => isSettingsCategoryVisible(key, {
        canManageUsers,
        isSuperAdmin,
        isClinicAdmin,
        isTcGroupAdmin,
        isTcGroupMember,
    }));
    const horizontal = variant === 'horizontal';
    const navRef = useRef(null);
    const activeRef = useRef(null);
    const [edges, setEdges] = useState({ start: false, end: false });
    // Which ends of the horizontal strip have more chips beyond them. Drives the
    // edge fade, the only cue that the strip scrolls (touch has no scrollbar).
    const measureEdges = useCallback(() => {
        const nav = navRef.current;
        if (!nav)
            return;
        const next = {
            start: nav.scrollLeft > 1,
            end: nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1,
        };
        setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
    }, []);
    // Keep the active category in view. A deep link (the viewer's hanging-protocol
    // shortcut, Layouts' "configure" link, /settings/users) used to open with the
    // active chip scrolled out of sight. scrollLeft on the strip itself, not
    // scrollIntoView, which would also scroll the dialog and page on iOS.
    useLayoutEffect(() => {
        if (!horizontal)
            return;
        const nav = navRef.current;
        const chip = activeRef.current;
        if (nav && chip) {
            const target = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2;
            nav.scrollLeft = Math.max(0, target);
        }
        measureEdges();
    }, [horizontal, activeCategory, visibleCategories.length, measureEdges]);
    useEffect(() => {
        if (!horizontal || typeof ResizeObserver === 'undefined')
            return;
        const nav = navRef.current;
        if (!nav)
            return;
        const ro = new ResizeObserver(measureEdges);
        ro.observe(nav);
        return () => ro.disconnect();
    }, [horizontal, measureEdges]);
    return (_jsx("nav", { ref: navRef, onScroll: horizontal ? measureEdges : undefined, "data-edge-start": horizontal && edges.start ? '' : undefined, "data-edge-end": horizontal && edges.end ? '' : undefined, className: cn('border-border bg-card/50', horizontal
            ? // relative: chip offsetLeft is measured against the strip. The
                // mask fades whichever end still has chips past it.
                'relative w-full shrink-0 overflow-x-auto border-b p-2 data-[edge-end]:[mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] data-[edge-start]:[mask-image:linear-gradient(to_right,transparent,black_2rem)] data-[edge-start]:data-[edge-end]:[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)] [@media(max-height:480px)]:mr-12 [@media(max-height:480px)]:w-auto [@media(max-height:480px)]:py-1'
            : 'w-56 shrink-0 overflow-y-auto border-r p-2', className), children: _jsx("ul", { className: cn(horizontal ? 'flex items-center gap-1' : 'space-y-1'), children: visibleCategories.map(({ key, labelKey }) => {
                const Icon = categoryIcons[key];
                const isActive = activeCategory === key;
                return (_jsx("li", { className: cn(horizontal && 'shrink-0'), children: _jsxs("button", { ref: isActive ? activeRef : undefined, type: "button", onClick: () => onCategoryChange(key), "aria-current": isActive ? 'page' : undefined, className: cn('flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium whitespace-nowrap transition-colors', horizontal && 'gap-2 pointer-coarse:min-h-10', isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground'), children: [_jsx(Icon, { className: "h-4 w-4 shrink-0" }), t(labelKey)] }) }, key));
            }) }) }));
}
